import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, FileText, Download, Clock, MapPin, User, Wrench } from 'lucide-react';
import api from '@/lib/api';

export default function WorkOrderView({ customerId }) {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWO, setSelectedWO] = useState(null);

  useEffect(() => {
    loadWorkOrders();
  }, []);

  const loadWorkOrders = async () => {
    try {
      setLoading(true);
      const res = await api.get('/work-orders');
      setWorkOrders(res.data);
    } catch (err) {
      console.error('Failed to load work orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-slate-100 text-slate-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      approved: 'bg-cyan-100 text-cyan-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const downloadPDF = async (woId) => {
    try {
      const response = await api.get(`/work-orders/${woId}/download-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `work_order_${woId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('PDF not available yet');
    }
  };

  const previewPDF = async (woId) => {
    try {
      const response = await api.get(`/work-orders/${woId}/preview-pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after a while to free memory
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Failed to preview PDF:', err);
      alert('Could not generate preview. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (selectedWO) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => setSelectedWO(null)} className="text-slate-600" data-testid="back-to-work-orders-btn">
            ← Back to Work Orders
          </Button>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => previewPDF(selectedWO.id)}
              variant="outline"
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50 rounded-full"
              data-testid="preview-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Preview PDF
            </Button>
            {selectedWO.pdf_url && (
              <Button
                onClick={() => downloadPDF(selectedWO.id)}
                className="bg-cyan-500 hover:bg-cyan-600 rounded-full"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
          </div>
        </div>

        <Card className="bg-white border-slate-200">
          <CardHeader className="border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-slate-800">Work Order Details</CardTitle>
                <p className="text-sm text-slate-500 mt-1">Ticket: {selectedWO.ticket_number}</p>
              </div>
              <Badge className={getStatusColor(selectedWO.status)}>
                {selectedWO.status.replace('_', ' ').toUpperCase()}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Customer Info */}
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Customer Information</h3>
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Name</p>
                  <p className="text-sm font-medium text-slate-800">{selectedWO.customer_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Email</p>
                  <p className="text-sm text-slate-600">{selectedWO.customer_email}</p>
                </div>
                {selectedWO.address && (
                  <div className="col-span-2">
                    <p className="text-xs text-slate-500 mb-1">Address</p>
                    <p className="text-sm text-slate-800">{selectedWO.address}</p>
                  </div>
                )}
                {selectedWO.person_called && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Person Called</p>
                    <p className="text-sm text-slate-800">{selectedWO.person_called}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Service Details */}
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Service Details</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Technician:</span>
                  <span className="font-medium text-slate-800">{selectedWO.technician_name}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Service Type:</span>
                  <Badge variant="outline">{selectedWO.remote_onsite}</Badge>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Time Spent:</span>
                  <span className="font-medium text-slate-800">{selectedWO.time_spent_hours} hours</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Wrench className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-600">Date:</span>
                  <span className="text-slate-800">{new Date(selectedWO.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            {/* Work Done */}
            {selectedWO.work_done && (
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Work Performed</h3>
                <p className="text-sm text-slate-700 whitespace-pre-wrap p-4 bg-slate-50 rounded-lg border border-slate-200">
                  {selectedWO.work_done}
                </p>
              </div>
            )}

            {/* Parts Used */}
            {selectedWO.parts_used && selectedWO.parts_used.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Parts Used</h3>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-slate-600">Part</th>
                        <th className="px-4 py-2 text-center text-slate-600">Quantity</th>
                        <th className="px-4 py-2 text-right text-slate-600">Cost</th>
                        <th className="px-4 py-2 text-right text-slate-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedWO.parts_used.map((part, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-slate-800">{part.name}</td>
                          <td className="px-4 py-2 text-center text-slate-600">{part.quantity}</td>
                          <td className="px-4 py-2 text-right text-slate-600">${part.cost.toFixed(2)}</td>
                          <td className="px-4 py-2 text-right font-medium text-slate-800">
                            ${(part.cost * part.quantity).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-slate-50 font-semibold">
                        <td colSpan="3" className="px-4 py-2 text-right text-slate-700">Total Cost:</td>
                        <td className="px-4 py-2 text-right text-slate-800">
                          ${selectedWO.parts_used.reduce((sum, p) => sum + p.cost * p.quantity, 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Signature */}
            {selectedWO.signature_image && (
              <div>
                <h3 className="font-semibold text-slate-800 mb-3">Signature</h3>
                <div className="border-2 border-slate-200 rounded-lg p-4 bg-white">
                  <img
                    src={selectedWO.signature_image}
                    alt="Signature"
                    className="max-w-full h-auto"
                  />
                </div>
              </div>
            )}

            {/* Approval Status */}
            {selectedWO.status === 'approved' && selectedWO.approval_comments && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-1">Admin Comments:</p>
                <p className="text-sm text-green-700">{selectedWO.approval_comments}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Work Orders</h2>
        <p className="text-slate-600">View work orders for your service tickets</p>
      </div>

      {workOrders.length === 0 ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-medium mb-2 text-slate-800">No work orders yet</h3>
            <p className="text-slate-500">Work orders will appear here when technicians complete service</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {workOrders.map((wo) => (
            <Card
              key={wo.id}
              className="bg-white border-slate-200 hover:border-cyan-500 hover:shadow-md transition-all cursor-pointer"
              onClick={() => setSelectedWO(wo)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-800">{wo.ticket_number}</h3>
                      <Badge className={getStatusColor(wo.status)}>
                        {wo.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mb-1">Technician: {wo.technician_name}</p>
                    <p className="text-sm text-slate-500">
                      {new Date(wo.created_at).toLocaleDateString()} • {wo.remote_onsite} • {wo.time_spent_hours}h
                    </p>
                  </div>
                  {wo.pdf_url ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadPDF(wo.id);
                      }}
                      className="text-cyan-600 border-cyan-500"
                      data-testid={`download-pdf-btn-${wo.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        previewPDF(wo.id);
                      }}
                      className="text-slate-600 border-slate-300"
                      data-testid={`preview-pdf-btn-${wo.id}`}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                  )}
                </div>
                {wo.work_done && (
                  <p className="text-sm text-slate-700 line-clamp-2">{wo.work_done}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
